// Закрыть свою учётную запись.
//
// ## Зачем это здесь, если записи заводит работодатель
//
// Правило магазина Apple 5.1.1(v): раз учётная запись в приложении есть, из приложения же
// должно быть видно, как её закрыть — письмом в поддержку это не заменяется. Спорить, что у
// нас доступ выдаёт администратор кодом приглашения, дешевле не выходит: проверяющий читает
// экран, а не наши рассуждения, и отказ по этой строке стоит целого круга проверки.
//
// ## Почему рядом со сменой своей фразы, а не в «Настройках»
//
// Ровно по той же причине, по которой там живёт смена фразы: «Настройки» открываются по праву
// `core.setting.view`, и у части ролей его нет. Своё имя в нижней части полосы разделов видно
// с любого экрана, значит и своим доступом человек распоряжается оттуда же.
//
// ## Почему окно называет последствия, а не спрашивает «вы уверены»
//
// Уверенность человека — не информация. Информация — что исчезнет и что останется. Здесь это
// особенно важно: «удалить учётную запись» звучит как «сотрите обо мне всё», а на деле
// остаются его смены, выдачи и подписи под ведомостями, потому что это бухгалтерия
// работодателя и хранить её обязан он. Обещать обратное было бы враньём, и человек обнаружил
// бы его в первый же день после нажатия.

import { useState } from 'react';

import { RiLogoutBoxRLine } from '@remixicon/react';

import { deleteMyself } from '@/shared/api/endpoints/users';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';

export function ЗакрытьСвоюЗаписьДиалог({
  открыт,
  закрыть,
  имяВхода,
  послеЗакрытия,
}: {
  открыт: boolean;
  закрыть: () => void;
  имяВхода: string;
  /** Уводит на экран входа: ключи уже мертвы, оставаться в программе не на чем. */
  послеЗакрытия: () => void | Promise<void>;
}) {
  const [занято, setЗанято] = useState(false);
  const [ошибка, setОшибка] = useState<unknown>(null);

  async function нажать() {
    if (занято) return;
    setЗанято(true);
    setОшибка(null);
    try {
      await deleteMyself();
      await послеЗакрытия();
    } catch (сбой) {
      // Заслоны сервера («последний вход в программу», «последний администратор») приезжают
      // сюда своими словами и с подсказкой, что сделать раньше. Пересказывать их здесь
      // своими — значит завести второй источник тех же текстов.
      setОшибка(сбой);
    } finally {
      setЗанято(false);
    }
  }

  return (
    <Modal.Root
      open={открыт}
      onOpenChange={(следующее) => {
        if (!следующее && !занято) закрыть();
      }}
    >
      <Modal.Content>
        <Modal.Header
          icon={RiLogoutBoxRLine}
          title='Закрыть свою учётную запись?'
          description={`Вход «${имяВхода}» перестанет работать сразу. Вернуть запись этим экраном будет нельзя.`}
        />
        <Modal.Body>
          <ul className='flex flex-col gap-1.5 text-paragraph-sm text-text-sub-600'>
            <li>Вы выйдете из программы на всех своих устройствах.</li>
            <li>Код для телефона, если он у вас на руках, перестанет работать.</li>
            <li>Ваше имя входа освободится: его смогут выдать другому человеку.</li>
            <li>
              Ваши смены, выдачи и отчёты останутся у работодателя: это его бухгалтерия, и без
              неё перестанут сходиться деньги.
            </li>
          </ul>
          <p className='mt-3 text-paragraph-xs text-text-sub-600'>
            Если доступ нужен снова, его заведёт заново администратор организации.
          </p>
          {ошибка !== null && ошибка !== undefined && (
            <ErrorAlert error={ошибка as never} className='mt-3' />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root variant='neutral' mode='stroke' size='small' className='w-full'>
              Отмена
            </Button.Root>
          </Modal.Close>
          <Button.Root
            variant='error'
            mode='filled'
            size='small'
            className='w-full'
            type='button'
            onClick={() => void нажать()}
          >
            {занято ? 'Закрываем…' : 'Закрыть запись'}
          </Button.Root>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
